import React, { useEffect, useMemo, useState } from "react";
import { useIntegrationGate } from "../../hooks/useIntegrationGate";
import { useCRMAuth } from "../../hooks/useCRMAuth";
import Modal from "../ui/modal";
import { fetchThirdPartyAccount } from "../../store/actions/companyActions";
import { useDispatch, useSelector } from "react-redux";
import AdminEnableAuth from "../Template/modal/AdminEnableAuth";
import { ENABLE_THIRD_PARTY_INTEGRATION_MODAL_CONTENT } from "../../constants/thirdParty";
import { decideIntegrationForProvider } from "../../_helpers/utils";
import { getConnectedProviders } from "../../store/actions/integrationActions";

const IntegrationModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const { modalState, closeModal } = useIntegrationGate();
  const connectedAccounts = useSelector(state => state.integration?.connectedProviders);
  const authorisedApps = useSelector(state => state.company?.thirdPartyAccounts?.details);
  const profilePermissionInfo = useSelector(state => state.company?.profilePermissionInfo);
  const companyId = useSelector(state => state.auth?.user?.company?.id);

  const providerName = modalState?.provider ?? null;

  const {
    isProcessing,
    isPopupOpen,
    showSuccessMessage,
    showFailureMessage,
    userAlreadyLinked,
    setUserAlreadyLinked,
    openAuthPopup,
    closeAuthPopup,
  } = useCRMAuth({ providerName });

  const [lockedDetails, setLockedDetails] = useState(null);

  const getBlockingType = linked => {
    if (!linked) return null;

    if (linked.adminRequired) {
      return "admin_required";
    }

    if (linked.orgMismatch) {
      return "organization_mismatch";
    }

    return "user_exists";
  };

  const BLOCKING_MODAL_TYPES = [
    "user_exists",
    "organization_mismatch",
    "admin_required",
];

  // pick modal details dynamically, but prefer lockedDetails when present
  const modalDetails = useMemo(() => {
    if (lockedDetails) return lockedDetails;
    if (userAlreadyLinked) {
      return ENABLE_THIRD_PARTY_INTEGRATION_MODAL_CONTENT[providerName].find(
        item => item.type === getBlockingType(userAlreadyLinked)
      );
    }
    return modalState?.modalDetails || {};
  }, [modalState?.modalDetails, userAlreadyLinked, lockedDetails]);

  // lock the UI when userAlreadyLinked is set by the CRM hook
  useEffect(() => {
    if (userAlreadyLinked) {
      const content = ENABLE_THIRD_PARTY_INTEGRATION_MODAL_CONTENT[providerName].find(
        item => item.type === getBlockingType(userAlreadyLinked)
      );
      if (content) setLockedDetails(content);
    }
    // do not clear lock here — we want it to persist until modal close
  }, [userAlreadyLinked]);

  useEffect(() => {
    // only run when modal is open
    if (!modalState?.open) return;

    if (userAlreadyLinked || (lockedDetails && BLOCKING_MODAL_TYPES.includes(lockedDetails.type))) {
      return;
    }

    const pending = modalState?.pendingPayload ?? modalState?.template?.data ?? modalState?.template;

    const providerKey =
      pending?.fieldData?.data_source?.provider ?? pending?.data_source?.provider ?? modalState?.provider;

    if (!providerKey) return;

    const isAdmin = ["Admin", "Super Admin"].includes(profilePermissionInfo?.name);

    const { stopCreating, modalContent } = decideIntegrationForProvider({
      providerKey,
      isAdmin,
      connectedAccountsArr: connectedAccounts,
      authorisedAppsArr: authorisedApps,
    });

    if (stopCreating) {
      // show computed modal content (if different)
      if (modalContent?.type && modalContent?.type !== lockedDetails?.type) {
        setLockedDetails(modalContent);
      }
    } else {
      // integration is now allowed — call pending onAllowed (if any) and close
      try {
        if (typeof modalState?.onAllowed === "function") {
          modalState.onAllowed({ success: true, payload: modalState.pendingPayload });
        }
      } catch (err) {
        console.error("IntegrationModal: onAllowed failed during recompute", err);
      }

      // refresh accounts & company info
      if (providerKey) {
        dispatch(fetchThirdPartyAccount(companyId));
        dispatch(getConnectedProviders());
      }

      // close manager modal
      closeModal();
    }
  }, [
    modalState?.pendingPayload,
    modalState?.provider,
    modalState?.open,
    connectedAccounts?.length,
    authorisedApps?.length,
    profilePermissionInfo?.name,
    userAlreadyLinked,
    lockedDetails?.type,
    companyId,
    closeModal,
  ]);

  // when CRM flow reports success -> call pending onAllowed and refresh accounts
  useEffect(() => {
    if (!showSuccessMessage) return;

    try {
      if (typeof modalState?.onAllowed === "function") {
        modalState.onAllowed({ success: true, payload: modalState.pendingPayload });
      }
    } catch (err) {
      console.error("IntegrationModal: onAllowed failed", err);
    }

    if (providerName) {
      // refresh integration accounts / company info
      dispatch(fetchThirdPartyAccount(companyId));
      dispatch(getConnectedProviders());
    }

    // close the manager modal (we'll clear local lock when modal actually closes)
    closeModal();
  }, [showSuccessMessage, modalState, providerName, closeModal, companyId]);

  // clear local lock when modal actually closes (so next opening can show fresh content)
  useEffect(() => {
    if (!modalState.open && lockedDetails) {
      setLockedDetails(null);
    }
  }, [modalState.open, lockedDetails]);

  // nothing to render if modal not open
  if (!modalState.open) return null;

  return (
    <Modal
      showModal={modalState.open}
      setShowModal={visible => {
        if (!visible) {
          // reset any local state (userAlreadyLinked) before closing
          if (userAlreadyLinked) setUserAlreadyLinked(false);
          // clear local lock too
          setLockedDetails(null);
          closeAuthPopup();
          closeModal();
          if (typeof onClose === "function") onClose();
        }
      }}
      component={AdminEnableAuth}
      permissionAdminModalContent={modalDetails}
      connectedAccounts={false}
      setModalButtonClicked={() => {
        // if the content is "user_exists" or "organization_mismatch" we don't attempt an auth flow — just close
        if (BLOCKING_MODAL_TYPES.includes(modalDetails?.type)) {
          // close modal and clear local state
          if (userAlreadyLinked) setUserAlreadyLinked(false);
          setLockedDetails(null);
          closeModal();
          return;
        }
        // otherwise start the auth flow
        // checkTokenStatus();
        openAuthPopup();
      }}
      isProcessing={isProcessing}
      isPopupOpen={isPopupOpen}
      showFailureMessage={showFailureMessage}
      userAlreadyLinked={userAlreadyLinked}
      setUserAlreadyLinked={setUserAlreadyLinked}
    />
  );
};

export default IntegrationModal;
